import { apiSlice } from "@/store/apis/api.slice"
import { merge } from "lodash"
import { getMiddlewareRoot } from "@/utils/helpers/configs"
import {
  UseLazyQuery,
  UseQuery,
} from "@reduxjs/toolkit/dist/query/react/buildHooks"
import { EndpointDefinitions, QueryDefinition } from "@reduxjs/toolkit/query"
import { OktaError, OktaUserDetails } from "@/types/Member"
import {
  EnrolledFactors,
  FactorChallenge,
  FactorChallengeResp,
  FactorValid,
  AuthenticatorsResponse,
  TransactionVerifyRequest,
  TransactionVerifyResponse,
  CodeExchangeRequest,
  CodeExchangeResponse,
  MfaInitRequest,
  MfaInitResponse,
} from "@/types/Okta"

const apiRoot = getMiddlewareRoot()

const idpApi = apiSlice.injectEndpoints({
  endpoints: (build) => {
    // idp specific endpoints
    const idpMap = [
      "user/factors",
      "user",
      "user/factor/valid",
      "myaccount/authenticators/me"
    ].reduce<EndpointDefinitions>((carry, key) => {
      const name = key.substring(key.lastIndexOf("/") + 1)
      carry[name] = build.query({
        query: () => `${apiRoot}/idp/${key}`,
        providesTags: [
          "Member",
          ...(name === "user"
            ? (["UserProfile"] as const)
            : name === "factors"
              ? (["UpdatePhone"] as const)
              : []),
        ] as const,
      })
      return carry
    }, {})

    return merge({}, idpMap, {
      factorChallenge: build.mutation<FactorChallengeResp, FactorChallenge>({
        query: (body) => ({
          url: `${apiRoot}/idp/user/factors`,
          method: "POST",
          body,
        }),
      }),
      updatePhone: build.mutation({
        query: ({ body }) => ({
          url: `${apiRoot}/update-phone`,
          body,
          method: "POST",
        }),
        invalidatesTags: ["UpdatePhone", "MemberDetails"],
      }),
      updatePhonePending: build.query({
        query: () => `${apiRoot}/update-phone/pending`,
        providesTags: ["UpdatePhone"],
      }),
      updatePhoneValidate: build.mutation({
        query: (body) => ({
          url: `${apiRoot}/update-phone/validate`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["UpdatePhone"],
      }),
      oktaProfile: build.mutation<OktaUserDetails[], { id: string }>({
        query: ({ id }) => ({
          url: `${apiRoot}/idp/user/${id}/id`,
          method: "POST",
        }),
      }),
      updateProfile: build.mutation({
        query: ({ body }) => ({
          url: `${apiRoot}/idp/user/update`,
          body,
          method: "POST",
        }),
        invalidatesTags: ["UserProfile"],
      }),
      verifyIdToken: build.mutation<
        TransactionVerifyResponse,
        TransactionVerifyRequest
      >({
        query: (body) => ({
          url: `${apiRoot}/idp/verify/id-token`,
          method: "POST",
          body,
        }),
      }),
      codeExchange: build.mutation<CodeExchangeResponse, CodeExchangeRequest>({
        query: (body) => ({
          url: `${apiRoot}/idp/code/exchange`,
          method: "POST",
          body,
        })
      }),
      mfaInit: build.mutation<MfaInitResponse, MfaInitRequest>({
        query: (body) => ({
          url: `${apiRoot}/idp/mfa/init`,
          method: "POST",
          body,
        }),
      }),
    })
  },
})

export type IdpApi = typeof idpApi & {
  useUserQuery: UseQuery<
    QueryDefinition<void, any, any, OktaUserDetails | OktaError>
  >
  useLazyUserQuery: UseLazyQuery<
    QueryDefinition<void, any, any, OktaUserDetails>
  >
  useFactorsQuery: UseQuery<QueryDefinition<void, any, any, EnrolledFactors>>
  useLazyValidQuery: UseLazyQuery<QueryDefinition<void, any, any, FactorValid>>
  useMeQuery: UseQuery<QueryDefinition<void, any, any, AuthenticatorsResponse>>
}

export const {
  useUserQuery,
  useLazyUserQuery,
  useFactorsQuery,
  useLazyValidQuery,
  useFactorChallengeMutation,
  useUpdatePhoneMutation,
  useUpdatePhonePendingQuery,
  useUpdatePhoneValidateMutation,
  useUpdateProfileMutation,
  useOktaProfileMutation,
  useMeQuery,
  useVerifyIdTokenMutation,
  useCodeExchangeMutation,
  useMfaInitMutation,
} = idpApi as IdpApi
